import type { FC, ReactNode } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  EluveLogo,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@eluve/components';

import type { MiniAppIdentity, SurfaceKind } from './types';

/**
 * The app's mark, shown beside the surface title so every mini-app surface is
 * visibly attributed to its publisher. First-party (Eluve) apps use the bundled
 * brand mark — no network fetch; third-party apps fall back to their declared
 * `logoUrl`.
 */
const MiniAppLogo: FC<{ identity: MiniAppIdentity }> = ({ identity }) => {
  if (identity.trustLevel === 'first-party') {
    return <EluveLogo className="h-5 w-5 shrink-0" />;
  }
  if (identity.logoUrl) {
    return (
      <img
        src={identity.logoUrl}
        alt=""
        className="h-5 w-5 shrink-0 rounded-sm"
      />
    );
  }
  return null;
};

/**
 * Renders a mini-app surface in the declared presentation — a side-panel
 * (`Sheet`) or a modal (`Dialog`). The SDK owns this host so apps don't each
 * re-implement modal/panel plumbing; they just supply content. The header shows
 * the app's logo + title so attribution is consistent across every surface.
 */
export const SurfaceHost: FC<{
  surface: SurfaceKind;
  title: string;
  identity: MiniAppIdentity;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}> = ({ surface, title, identity, open, onOpenChange, children }) => {
  if (surface === 'sidepanel') {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="flex w-[480px] flex-col sm:max-w-[480px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <MiniAppLogo identity={identity} />
              {title}
            </SheetTitle>
          </SheetHeader>
          {/* Body fills the remaining height (header is fixed at top). The
              surface owns its own scrolling, pinned footer, and horizontal
              padding — so a scrolling surface can run its scroll viewport
              edge-to-edge and keep the scrollbar pinned to the panel edge
              instead of floating inside an inset gutter. */}
          <div className="flex min-h-0 flex-1 flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MiniAppLogo identity={identity} />
            {title}
          </DialogTitle>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
};
